import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState, useEffect } from "react";
import { LoopingImage, useToast } from "@/components/common";
import styles from "../client/jobs/ClientJobsPage.module.css";
import surveyorStyles from "./SurveyorJobsPage.module.css";
import tableStyles from "../admin/contacts/components/ClientJobsTab.module.css";
import { ViewJobModal } from "./components";
import { useAuth } from "@/context/AuthContext";
import { jobsService } from "@/api/services/jobs.service";
import { sitesService } from "@/api/services/sites.service";
import { formatDateOnlyAUS, formatDateTimeAUS, getTimeFromIso, resolveDisplayTime, } from "@/utils/dateTime";
import { formatJobAddress } from "@/utils/address";
import { jobToUpdateDto, normaliseJob } from "@/utils/jobUtils";
const BUILDING_TYPE_LABELS = {
    COMMERCIAL: "Commercial",
    RETAIL: "Retail",
    HOSPITALITY: "Hospitality",
    INDUSTRIAL: "Industrial",
};
const getJobSortTimestamp = (job) => {
    const candidate = job.scheduledDate ||
        job.commencementDate ||
        job.updatedAt ||
        job.createdAt ||
        null;
    if (!candidate)
        return 0;
    const ts = new Date(candidate).getTime();
    return Number.isFinite(ts) ? ts : 0;
};
const getScheduledDateForDisplay = (job) => job.scheduledDate ?? job.commencementDate ?? null;
const getBuildingPermitStatus = (job) => {
    if (job.cfiApproved)
        return "none";
    if (job.cfiRequested)
        return "cfi_requested";
    if (job.buildingPermitApproved)
        return "uploaded";
    if (job.buildingPermitRequested)
        return "requested";
    return "none";
};
const LocationIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" }), _jsx("circle", { cx: "12", cy: "10", r: "3" })] }));
const CalendarIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" })] }));
const ToolIcon = () => (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" }) }));
const formatJobDate = (dateString) => {
    const formatted = formatDateTimeAUS(dateString);
    return formatted === "—" ? "TBD" : formatted;
};
const getBuildingPermitUploadDateDisplay = (job) => {
    const date = job.buildingPermitApprovedDate ?? job.buildingPermitRequestedDate ?? null;
    const formatted = formatDateOnlyAUS(date);
    return formatted === "—" ? "-" : formatted;
};
const getCfiUploadDateDisplay = (job) => {
    const date = job.cfiApprovedDate ?? job.cfiRequestedDate ?? null;
    const formatted = formatDateOnlyAUS(date);
    return formatted === "—" ? "-" : formatted;
};
const getBuildingTypeDisplay = (job, siteBuildingTypes) => {
    const types = job.buildingTypes ?? job.site?.buildingTypes ?? null;
    if (types?.length) {
        const first = types[0];
        return BUILDING_TYPE_LABELS[first] ?? first;
    }
    if (job.siteId && job.siteId in siteBuildingTypes) {
        return siteBuildingTypes[job.siteId];
    }
    return "-";
};
export const SurveyorJobsPage = () => {
    const { user } = useAuth();
    const toast = useToast();
    const surveyorId = user?.id;
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [siteBuildingTypes, setSiteBuildingTypes] = useState({});
    const [siteTenancyData, setSiteTenancyData] = useState({});
    const [activeTab, setActiveTab] = useState("new-updates");
    const fetchJobs = useCallback(async () => {
        if (!surveyorId) {
            setError("No surveyor ID found. Please log in again.");
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            setError(null);
            const response = await jobsService.getJobsBySurveyor(surveyorId);
            const data = response.data;
            let list = [];
            if (Array.isArray(data)) {
                list = data.map(normaliseJob);
            }
            else if (data && typeof data === "object") {
                const wrapped = data;
                const arr = wrapped.data ?? wrapped.jobs ?? wrapped.items;
                list = Array.isArray(arr) ? arr.map(normaliseJob) : [];
            }
            setJobs(list);
        }
        catch (err) {
            console.error("Failed to fetch surveyor jobs:", err);
            setError("Failed to load jobs. Please try again.");
        }
        finally {
            setIsLoading(false);
        }
    }, [surveyorId]);
    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);
    const jobsBySection = useMemo(() => {
        const list = Array.isArray(jobs) ? jobs : [];
        const activeJobs = list.filter((j) => j.status !== "cancelled");
        // New Job Updates: surveyor has not yet accepted/declined (PENDING)
        const pending = activeJobs
            .filter((j) => j.surveyorStatus === "PENDING" || j.surveyorStatus == null)
            .sort((a, b) => getJobSortTimestamp(b) - getJobSortTimestamp(a));
        const surveyorApprovedJobs = activeJobs.filter((j) => j.surveyorStatus === "APPROVED");
        // Jobs in Progress – Building Permit Requested: surveyor accepted, permit not yet uploaded
        const permitRequested = surveyorApprovedJobs
            .filter((j) => j.buildingPermitRequested &&
            !j.buildingPermitApproved &&
            !j.cfiRequested &&
            !j.cfiApproved)
            .sort((a, b) => getJobSortTimestamp(a) - getJobSortTimestamp(b));
        // Jobs in Progress – Building Permit Uploaded: permit uploaded, CFI not yet requested
        const permitUploaded = surveyorApprovedJobs
            .filter((j) => j.buildingPermitApproved && !j.cfiRequested && !j.cfiApproved)
            .sort((a, b) => getJobSortTimestamp(a) - getJobSortTimestamp(b));
        const cfiRequested = surveyorApprovedJobs
            .filter((j) => j.cfiRequested && !j.cfiApproved)
            .sort((a, b) => getJobSortTimestamp(a) - getJobSortTimestamp(b));
        // Job History: CFI approved or job completed (surveyor flow complete)
        const history = surveyorApprovedJobs
            .filter((j) => j.cfiApproved || j.status === "completed")
            .sort((a, b) => getJobSortTimestamp(b) - getJobSortTimestamp(a));
        return { pending, permitRequested, permitUploaded, cfiRequested, history };
    }, [jobs]);
    // Fetch site building types for Job History (jobs without buildingTypes on job)
    useEffect(() => {
        const history = jobsBySection.history;
        const siteIdsToFetch = [
            ...new Set(history
                .filter((j) => j.siteId &&
                !(j.buildingTypes?.length) &&
                !(j.siteId in siteBuildingTypes))
                .map((j) => j.siteId)),
        ];
        if (siteIdsToFetch.length === 0)
            return;
        let cancelled = false;
        Promise.all(siteIdsToFetch.map((id) => sitesService.getSite(id)))
            .then((responses) => {
            if (cancelled)
                return;
            setSiteBuildingTypes((prev) => {
                const next = { ...prev };
                responses.forEach((res, i) => {
                    const siteId = siteIdsToFetch[i];
                    const types = res.data.buildingTypes;
                    if (siteId) {
                        next[siteId] =
                            types?.length
                                ? BUILDING_TYPE_LABELS[types[0]] ?? types[0]
                                : "-";
                    }
                });
                return next;
            });
        })
            .catch(() => { });
        return () => {
            cancelled = true;
        };
    }, [jobsBySection.history]);
    // Fetch site tenancy data for job cards and modal
    useEffect(() => {
        const allJobs = [
            ...jobsBySection.pending,
            ...jobsBySection.permitRequested,
            ...jobsBySection.permitUploaded,
            ...jobsBySection.cfiRequested,
            ...jobsBySection.history,
        ];
        const siteIdsToFetch = [
            ...new Set(allJobs
                .filter((j) => j.siteId && !(j.siteId in siteTenancyData))
                .map((j) => j.siteId)),
        ];
        if (siteIdsToFetch.length === 0)
            return;
        let cancelled = false;
        Promise.all(siteIdsToFetch.map((id) => sitesService.getSite(id)))
            .then((responses) => {
            if (cancelled)
                return;
            setSiteTenancyData((prev) => {
                const next = { ...prev };
                responses.forEach((res, i) => {
                    const siteId = siteIdsToFetch[i];
                    const site = res.data;
                    if (siteId && site) {
                        next[siteId] = {
                            tenancyNumber: site.tenancyNumber ?? "",
                            tenancyName: site.tenancyName ?? "",
                            shoppingCentre: site.shoppingCenter ?? site.contactPerson?.shoppingCenter ?? "",
                        };
                    }
                });
                return next;
            });
        })
            .catch(() => { });
        return () => {
            cancelled = true;
        };
    }, [
        jobsBySection.pending,
        jobsBySection.permitRequested,
        jobsBySection.permitUploaded,
        jobsBySection.cfiRequested,
        jobsBySection.history,
        siteTenancyData,
    ]);
    const handleViewJob = (jobId) => {
        const list = Array.isArray(jobs) ? jobs : [];
        const job = list.find((j) => j.id === jobId);
        if (job) {
            setSelectedJob(job);
            setIsModalOpen(true);
        }
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedJob(null), 300);
    };
    const handleAcceptJob = async (job) => {
        if (!surveyorId)
            return;
        try {
            setProcessingId(job.id);
            await jobsService.surveyorAcceptJob(job.id, surveyorId);
            const res = await jobsService.getJob(job.id);
            const latestJob = normaliseJob(res.data);
            const dto = jobToUpdateDto(latestJob, { surveyorStatus: "APPROVED" });
            await jobsService.updateJob(job.id, dto);
            await fetchJobs();
        }
        catch (err) {
            console.error("Failed to accept job:", err);
            setError("Failed to accept job. Please try again.");
        }
        finally {
            setProcessingId(null);
        }
    };
    const handleDeclineJob = async (job) => {
        if (!surveyorId)
            return;
        try {
            setProcessingId(job.id);
            await jobsService.surveyorDeclineJob(job.id, surveyorId);
            const res = await jobsService.getJob(job.id);
            const latestJob = normaliseJob(res.data);
            const dto = jobToUpdateDto(latestJob, { surveyorStatus: "DECLINED" });
            await jobsService.updateJob(job.id, dto);
            await fetchJobs();
            if (selectedJob?.id === job.id)
                handleCloseModal();
        }
        catch (err) {
            console.error("Failed to decline job:", err);
            let errorMessage = "Failed to decline job. Please try again.";
            if (err && typeof err === "object" && "response" in err) {
                const axiosError = err;
                if (axiosError.response?.data?.message) {
                    errorMessage = axiosError.response.data.message;
                }
            }
            toast.error(errorMessage, 5000);
        }
        finally {
            setProcessingId(null);
        }
    };
    const newJobUpdates = jobsBySection.pending;
    const permitRequestedJobs = jobsBySection.permitRequested;
    const permitUploadedJobs = jobsBySection.permitUploaded;
    const cfiRequestedJobs = jobsBySection.cfiRequested;
    const jobHistory = jobsBySection.history;
    const renderJobCard = (job, index) => {
        return (_jsxs("article", { className: `${styles.jobCard} ${surveyorStyles.jobCard}`, children: [_jsx("div", { className: styles.jobImage, children: job.imageUrl ? (_jsx("img", { src: job.imageUrl, alt: `Job ${job.jobNumber}`, onError: (e) => {
                            e.currentTarget.src =
                                "/images/Defit Social Media 3.png";
                        } })) : index !== undefined ? (_jsx(LoopingImage, { imagePrefix: "/images/request-quote-default", count: index + 1, fallbackImage: "/images/empty-state-job.png", className: styles.jobLoopingImage })) : (_jsx("div", { className: styles.jobImagePlaceholder, children: _jsx(ToolIcon, {}) })) }), _jsxs("div", { className: styles.jobDetails, children: [_jsx("div", { className: styles.jobHeader, children: _jsxs("span", { className: styles.jobTitle, children: ["Job #", job.jobNumber] }) }), _jsxs("div", { className: `${styles.jobInfoGrid} ${surveyorStyles.jobInfoGrid2}`, children: [_jsxs("div", { className: styles.jobInfoItem, children: [_jsx(LocationIcon, {}), _jsx("span", { children: formatJobAddress(job) })] }), _jsxs("div", { className: styles.jobInfoItem, children: [_jsx(CalendarIcon, {}), _jsx("span", { children: formatJobDate(getScheduledDateForDisplay(job)) })] }), job.siteId && siteTenancyData[job.siteId] && (_jsxs(_Fragment, { children: [siteTenancyData[job.siteId].tenancyNumber ? (_jsx("div", { className: styles.jobInfoItem, children: _jsxs("span", { children: ["Tenancy #: ", siteTenancyData[job.siteId].tenancyNumber] }) })) : null, siteTenancyData[job.siteId].tenancyName ? (_jsx("div", { className: styles.jobInfoItem, children: _jsxs("span", { children: ["Tenancy: ", siteTenancyData[job.siteId].tenancyName] }) })) : null, siteTenancyData[job.siteId].shoppingCentre ? (_jsx("div", { className: styles.jobInfoItem, children: _jsxs("span", { children: ["Centre: ", siteTenancyData[job.siteId].shoppingCentre] }) })) : null] }))] }), _jsx("div", { className: styles.jobActions, children: _jsxs("button", { className: `${styles.viewJobBtn} ${surveyorStyles.viewJobBtn}`, onClick: () => handleViewJob(job.id), children: [_jsx(ToolIcon, {}), "VIEW JOB"] }) })] })] }, job.id));
    };
    // Convert Job to ViewJobModal job format (start/end dates and times with fallback from ISO)
    const commencementDate = selectedJob?.commencementDate ?? selectedJob?.scheduledDate ?? null;
    const completionDate = selectedJob?.completionDate ??
        selectedJob?.commencementDate ??
        selectedJob?.scheduledDate ??
        null;
    const selectedTenancy = selectedJob?.siteId && siteTenancyData[selectedJob.siteId]
        ? siteTenancyData[selectedJob.siteId]
        : null;
    const modalJob = selectedJob
        ? {
            id: selectedJob.id,
            jobNumber: selectedJob.jobNumber,
            title: selectedJob.title,
            location: formatJobAddress(selectedJob),
            scheduledDate: getScheduledDateForDisplay(selectedJob),
            commencementDate,
            completionDate,
            startTime: resolveDisplayTime(commencementDate, selectedJob.startTime, getTimeFromIso(commencementDate)),
            endTime: resolveDisplayTime(completionDate, selectedJob.endTime, getTimeFromIso(completionDate)),
            jobScope: selectedJob.scope ?? undefined,
            jobDescription: selectedJob.description ?? undefined,
            buildingPermitStatus: getBuildingPermitStatus(selectedJob),
            siteId: selectedJob.siteId ?? undefined,
            tenancyNumber: selectedTenancy?.tenancyNumber ?? undefined,
            tenancyName: selectedTenancy?.tenancyName ?? undefined,
            shoppingCentre: selectedTenancy?.shoppingCentre ?? undefined,
        }
        : null;
    if (isLoading) {
        return (_jsx("div", { className: surveyorStyles.page, children: _jsx("div", { className: styles.grid, children: _jsx("div", { className: `${styles.span12} ${styles.emptySection}`, children: "Loading jobs..." }) }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(ViewJobModal, { isOpen: isModalOpen, onClose: handleCloseModal, job: modalJob, onRefresh: fetchJobs, fullJob: selectedJob }), _jsxs("div", { className: surveyorStyles.page, children: [error && (_jsxs("div", { className: surveyorStyles.errorBlock, children: [_jsx("span", { children: error }), _jsx("button", { className: styles.viewJobBtn, onClick: fetchJobs, children: "Retry" })] })), _jsx("nav", { className: surveyorStyles.navTabs, "aria-label": "Job sections", children: _jsxs("div", { className: surveyorStyles.tabList, role: "tablist", children: [_jsx("button", { type: "button", role: "tab", "aria-selected": activeTab === "new-updates", className: `${surveyorStyles.tabButton} ${activeTab === "new-updates" ? surveyorStyles.active : ""}`, onClick: () => setActiveTab("new-updates"), children: "New Job Updates" }), _jsx("button", { type: "button", role: "tab", "aria-selected": activeTab === "in-progress", className: `${surveyorStyles.tabButton} ${activeTab === "in-progress" ? surveyorStyles.active : ""}`, onClick: () => setActiveTab("in-progress"), children: "Jobs In Progress" }), _jsx("button", { type: "button", role: "tab", "aria-selected": activeTab === "cfi-requested", className: `${surveyorStyles.tabButton} ${activeTab === "cfi-requested" ? surveyorStyles.active : ""}`, onClick: () => setActiveTab("cfi-requested"), children: "CFI Requested" })] }) }), _jsxs("div", { className: surveyorStyles.tabContentArea, children: [activeTab === "new-updates" && (_jsx(_Fragment, { children: newJobUpdates.length === 0 ? (_jsx("div", { className: styles.emptySection, children: "No new job updates at the moment." })) : (_jsx("div", { className: `${styles.jobsGrid} ${surveyorStyles.newJobUpdatesGrid}`, children: newJobUpdates.map((job, i) => (_jsxs("div", { className: `${styles.jobCard} ${surveyorStyles.jobCard}`, children: [_jsx("div", { className: styles.jobImage, children: job.imageUrl ? (_jsx("img", { src: job.imageUrl, alt: `Job ${job.jobNumber}`, onError: (e) => {
                                                        e.currentTarget.src =
                                                            "/images/Defit Social Media 3.png";
                                                    } })) : (_jsx(LoopingImage, { imagePrefix: "/images/request-quote-default", count: i + 1, fallbackImage: "/images/empty-state-job.png", className: styles.jobLoopingImage })) }), _jsxs("div", { className: styles.jobDetails, children: [_jsx("div", { className: styles.jobHeader, children: _jsxs("div", { className: styles.jobTitle, children: ["Job #", job.jobNumber] }) }), _jsxs("div", { className: surveyorStyles.jobInfoGrid, children: [_jsxs("div", { className: styles.jobInfoItem, children: [_jsx(LocationIcon, {}), _jsx("span", { children: formatJobAddress(job) })] }), _jsxs("div", { className: styles.jobInfoItem, children: [_jsx(CalendarIcon, {}), _jsx("span", { children: formatJobDate(getScheduledDateForDisplay(job)) })] }), job.siteId && siteTenancyData[job.siteId] && (_jsxs(_Fragment, { children: [siteTenancyData[job.siteId].tenancyNumber ? (_jsx("div", { className: styles.jobInfoItem, children: _jsxs("span", { children: ["Tenancy #:", " ", siteTenancyData[job.siteId].tenancyNumber] }) })) : null, siteTenancyData[job.siteId].tenancyName ? (_jsx("div", { className: styles.jobInfoItem, children: _jsxs("span", { children: ["Tenancy:", " ", siteTenancyData[job.siteId].tenancyName] }) })) : null, siteTenancyData[job.siteId].shoppingCentre ? (_jsx("div", { className: styles.jobInfoItem, children: _jsxs("span", { children: ["Centre:", " ", siteTenancyData[job.siteId].shoppingCentre] }) })) : null] }))] }), _jsxs("div", { className: surveyorStyles.newJobCardActions, children: [_jsx("div", { className: surveyorStyles.newJobCardActionsLeft, children: _jsxs("button", { className: `${styles.viewJobBtn} ${surveyorStyles.viewJobBtn}`, onClick: () => handleViewJob(job.id), disabled: processingId === job.id, children: [_jsx(ToolIcon, {}), "VIEW JOB"] }) }), _jsxs("div", { className: surveyorStyles.newJobCardActionsRight, children: [_jsx("button", { className: surveyorStyles.acceptBtn, onClick: () => handleAcceptJob(job), disabled: processingId === job.id, children: "ACCEPT" }), _jsx("button", { className: surveyorStyles.declineBtn, onClick: () => handleDeclineJob(job), disabled: processingId === job.id, children: "DECLINE" })] })] })] })] }, job.id))) })) })), activeTab === "in-progress" && (_jsxs("div", { className: surveyorStyles.inProgressSections, children: [_jsxs("section", { className: surveyorStyles.inProgressSection, children: [_jsx("div", { className: surveyorStyles.panelHeader, children: _jsx("h2", { className: surveyorStyles.panelTitle, children: "Building Permit Requested" }) }), _jsx("div", { className: surveyorStyles.jobsContainer, children: permitRequestedJobs.length === 0 ? (_jsx("div", { className: styles.emptySection, children: "No jobs in this section." })) : (_jsx("div", { className: `${styles.jobList} ${surveyorStyles.jobListSurveyor} ${surveyorStyles.jobsInProgress}`, children: permitRequestedJobs.map((job, i) => renderJobCard(job, i)) })) })] }), _jsxs("section", { className: surveyorStyles.inProgressSection, children: [_jsx("div", { className: surveyorStyles.panelHeader, children: _jsx("h2", { className: surveyorStyles.panelTitle, children: "Building Permit Uploaded" }) }), _jsx("div", { className: surveyorStyles.jobsContainer, children: permitUploadedJobs.length === 0 ? (_jsx("div", { className: styles.emptySection, children: "No jobs in this section." })) : (_jsx("div", { className: `${styles.jobList} ${surveyorStyles.jobListSurveyor} ${surveyorStyles.jobsInProgress}`, children: permitUploadedJobs.map((job, i) => renderJobCard(job, i)) })) })] })] })), activeTab === "cfi-requested" && (_jsx("div", { className: surveyorStyles.jobsContainer, children: cfiRequestedJobs.length === 0 ? (_jsx("div", { className: styles.emptySection, children: "No CFI requests at the moment." })) : (_jsx("div", { className: `${styles.jobList} ${surveyorStyles.jobListSurveyor} ${surveyorStyles.jobsOnHold}`, children: cfiRequestedJobs.map((job, i) => renderJobCard(job, i)) })) }))] }), _jsxs("section", { className: `${styles.span12} ${surveyorStyles.jobHistoryPanel}`, children: [_jsx("div", { className: surveyorStyles.panelHeader, children: _jsx("h2", { className: surveyorStyles.panelTitle, children: "Job History" }) }), _jsx("div", { className: tableStyles.tablePaper, children: _jsx("div", { className: tableStyles.tableContainer, children: _jsxs("table", { className: tableStyles.table, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: tableStyles.thFirst, children: "Job #" }), _jsx("th", { children: "Site Address" }), _jsx("th", { children: "Building Permit Upload Date" }), _jsx("th", { children: "CFI Upload Date" }), _jsx("th", { children: "Building Type" }), _jsx("th", { className: tableStyles.thLast })] }) }), _jsx("tbody", { children: jobHistory.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 6, className: tableStyles.emptyState, children: "No historical jobs yet." }) })) : (jobHistory.map((job) => (_jsxs("tr", { children: [_jsxs("td", { className: tableStyles.tdFirst, children: ["#", job.jobNumber] }), _jsx("td", { children: formatJobAddress(job) }), _jsx("td", { children: getBuildingPermitUploadDateDisplay(job) }), _jsx("td", { children: getCfiUploadDateDisplay(job) }), _jsx("td", { children: getBuildingTypeDisplay(job, siteBuildingTypes) }), _jsx("td", { className: tableStyles.tdActions, children: _jsx("button", { className: `${styles.viewJobBtn} ${surveyorStyles.viewJobBtnTable}`, onClick: () => handleViewJob(job.id), children: "VIEW JOB" }) })] }, job.id)))) })] }) }) })] })] })] }));
};
